import {
  Button,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { Trash2Icon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface ConfigureContentElementFooterActionsProps {
  saving: boolean;
  canSave: boolean;
  deleteBlockedReason: 'locked' | 'unresolvable' | null;
  onClose: () => void;
  onSave: () => void;
  onRequestDelete: () => void;
}

/**
 * Delete / Cancel / Save actions shared verbatim by the Dialog and Sheet footers. Deletes the
 * ELEMENT — untagging is the Section Name field's job, so a trash that only stripped an attribute
 * would read as destruction and leave users expecting the element to go.
 */
export function ConfigureContentElementFooterActions({
  saving,
  canSave,
  deleteBlockedReason,
  onClose,
  onSave,
  onRequestDelete,
}: ConfigureContentElementFooterActionsProps) {
  const { t } = useTranslation('admin');

  return (
    <>
      {deleteBlockedReason ? (
        /* Its own provider: this modal is exported from the feature's public API, and a missing
           ancestor provider is a hard throw rather than a degraded tooltip. Providers nest, so the
           editor page having one already costs nothing. */
        <TooltipProvider>
          <Tooltip>
            {/* A disabled button fires no pointer events, so the tooltip needs a wrapper to hang on
                — and that wrapper carries the tab stop the disabled button cannot. Without it a
                keyboard user could never reach the explanation for why deletion is unavailable,
                which is the whole reason this is disabled rather than hidden. */}
            <TooltipTrigger asChild>
              {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- see above */}
              <span tabIndex={0} data-testid="delete-element-tooltip-trigger">
                <Button
                  variant="outline"
                  size="icon"
                  aria-label={t('templatesConfig.configureElement.deleteElement')}
                  disabled
                >
                  <Trash2Icon className="size-4" />
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {deleteBlockedReason === 'locked'
                ? t('templatesConfig.configureElement.cannotDeleteLockedElement')
                : t('templatesConfig.configureElement.cannotLocateElement')}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <Button
          variant="outline"
          size="icon"
          aria-label={t('templatesConfig.configureElement.deleteElement')}
          disabled={saving}
          onClick={onRequestDelete}
        >
          <Trash2Icon className="size-4" />
        </Button>
      )}
      <div className="ml-auto flex gap-2">
        <Button variant="outline" onClick={onClose} disabled={saving}>
          {t('templatesConfig.configureElement.cancel')}
        </Button>
        <Button onClick={onSave} disabled={!canSave}>
          {saving && <Spinner />}
          {t('templatesConfig.configureElement.save')}
        </Button>
      </div>
    </>
  );
}
